import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { enableDemoMode, isDemoMode } from '../../services/mockData';
import WorkspaceScene from './WorkspaceScene';
import MicrosoftSignInButton from './MicrosoftSignInButton';
import './LoginForm.css';

interface LoginFormProps {
  onLogin?: (email: string, password: string) => void;
  onSuccess?: () => void;
}

/**
 * Login page.
 *
 * ── UI vs. auth ───────────────────────────────────────────────────────────────
 * This file owns the visual composition — a branding band on top, then the
 * animated workspace illustration and a premium sign-in card side by side. The
 * authentication itself is unchanged: Microsoft Entra ID SSO through
 * `signInWithAzureAD`, Cognito through `signIn`/`completeNewPassword`, the
 * session restore gate (`authLoading`), the demo-mode path and all error and
 * redirect handling are exactly as before. The redesign only moves where these
 * are rendered and how they look.
 */
/**
 * Every module the sidebar offers, in the order it lists them.
 *
 * Kept as data so adding a module is one entry here. Icons are stroke paths drawn into a shared
 * 24px frame, so the grid reads as one set.
 */
const MODULES: { title: string; description: string; icon: React.ReactNode }[] = [
  { title: 'Expenses', description: 'Submit, approve and reimburse claims', icon: <><rect x="2" y="6" width="20" height="14" rx="2" /><path d="M16 13h2" /><path d="M2 10h20" /></> },
  { title: 'Leave', description: 'Apply, approve, comp-off and policies', icon: <><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /><path d="M9 16l2 2 4-4" /></> },
  { title: 'Attendance', description: 'Device punches and regularization', icon: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3.5 2" /></> },
  { title: 'Shift Roster', description: 'Plan shifts and bulk overrides', icon: <><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M3 10h18M9 4v18" /></> },
  { title: 'Timesheets', description: 'Log hours against projects and tasks', icon: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6M8 13h8M8 17h5" /></> },
  { title: 'Performance', description: 'Goals, reviews and appraisal cycles', icon: <><path d="M3 3v18h18" /><path d="M7 15l4-4 3 3 5-6" /></> },
  { title: 'Certifications', description: 'Track certificates and reimbursements', icon: <><circle cx="12" cy="9" r="6" /><path d="M8.5 14L7 22l5-3 5 3-1.5-8" /></> },
  { title: 'Award Nominations', description: 'SPOT awards and quarterly excellence', icon: <path d="M12 2l3.1 6.3 6.9 1-5 4.9 1.2 6.8L12 17.8 5.8 21l1.2-6.8-5-4.9 6.9-1z" /> },
  { title: 'Voting Polls', description: 'Run polls and team decisions', icon: <><path d="M9 11l3 3 8-8" /><path d="M20 12v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h9" /></> },
  { title: 'Employee Profiles', description: 'Onboarding, documents and records', icon: <><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="9" cy="11" r="2.5" /><path d="M5.5 17a3.5 3.5 0 0 1 7 0M15 9h3M15 13h3" /></> },
  { title: 'Teams & Users', description: 'Roles, managers and org hierarchy', icon: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8" /></> },
  { title: 'Ask Creyon', description: 'AI assistant for your HR questions', icon: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /> },
];

const LoginForm: React.FC<LoginFormProps> = ({ onLogin, onSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showNewPasswordForm, setShowNewPasswordForm] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  // Set by the inactivity guard when it signs someone out (navigate('/login', { state: { reason } })).
  const signedOutForInactivity = (location.state as { reason?: string } | null)?.reason === 'inactivity';

  const { signIn, signInWithAzureAD, completeNewPassword, isDemoMode: isDemo, needsNewPassword, isSSOEnabled, isAuthenticated, isLoading: authLoading } = useAuth();

  // Redirect to home page if already authenticated
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      console.log('[LoginForm] User already authenticated, redirecting to home');
      navigate('/home');
    }
  }, [isAuthenticated, authLoading, navigate]);

  /*
   * Restoring a session is not the same as being idle.
   *
   * AuthContext starts with isLoading true and only then discovers whether Cognito still has
   * a live session. authLoading previously gated the redirect above but not the form, so a
   * submit during that window raced the restore — Amplify threw "There is already a signed in
   * user", it was rendered as a red login error, and then the restore finished and redirected
   * to /home regardless. AuthService now resolves that conflict instead of throwing; this
   * stops the race being started at all. Only the submit buttons wait — the fields stay
   * typeable, so a cold load costs nothing but the moment before the button arms.
   */
  const busy = isLoading || authLoading;

  const handleSSOLogin = async () => {
    setError('');
    setIsLoading(true);
    try {
      await signInWithAzureAD();
      // Redirect will happen automatically
    } catch (err: any) {
      console.error('SSO login error:', err);
      setError(err.message || 'SSO login failed. Please try again.');
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (onLogin) {
        onLogin(email, password);
      } else {
        const result = await signIn(email, password);
        if (result.needsNewPassword) {
          setShowNewPasswordForm(true);
        } else {
          onSuccess?.();
          navigate('/home');
        }
      }
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);
    try {
      await completeNewPassword(newPassword);
      navigate('/home');
    } catch (err: any) {
      console.error('New password error:', err);
      setError(err.message || 'Failed to set new password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDemoLogin = async (role: 'employee' | 'manager' | 'admin') => {
    enableDemoMode();
    const emails = {
      employee: 'employee@demo.com',
      manager: 'manager@demo.com',
      admin: 'admin@demo.com'
    };
    setEmail(emails[role]);
    setPassword('demo123');

    // Auto-submit after setting credentials
    setIsLoading(true);
    setError('');
    try {
      await signIn(emails[role], 'demo123');
      navigate('/home');
    } catch (err: any) {
      setError(err.message || 'Demo login failed.');
    } finally {
      setIsLoading(false);
    }
  };

  const demoActive = isDemo || isDemoMode();

  // Time-aware copy so the page greets people for their part of the workday.
  const hourNow = new Date().getHours();
  const period = hourNow < 12 ? 'Morning' : hourNow < 17 ? 'Afternoon' : 'Evening';
  const greeting = `Good ${period.toLowerCase()}`;
  const signInSub =
    hourNow < 12 ? 'Sign in to start your day.'
    : hourNow < 17 ? 'Sign in to pick up where you left off.'
    : 'Sign in to log today’s hours before you head out.';

  /** The right-hand card's body, which depends on which flow is active. */
  let cardBody: React.ReactNode;

  if (showNewPasswordForm || needsNewPassword) {
    cardBody = (
      <>
        <h1 className="login-card__title">Set a new password</h1>
        <p className="login-card__subtitle">Your temporary password has expired. Please choose a new one.</p>

        <form onSubmit={handleNewPasswordSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="newPassword">New Password</label>
            <input
              type="password"
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              placeholder="Enter new password"
              disabled={isLoading}
              minLength={8}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              placeholder="Confirm new password"
              disabled={isLoading}
              minLength={8}
            />
          </div>

          <div className="password-requirements">
            <p>Password must contain:</p>
            <ul>
              <li>At least 8 characters</li>
              <li>Uppercase and lowercase letters</li>
              <li>At least one number</li>
              <li>At least one special character</li>
            </ul>
          </div>

          {error && <div className="error-message" role="alert">{error}</div>}

          <button
            type="submit"
            disabled={isLoading || !newPassword || !confirmPassword}
            className="login-button"
          >
            {isLoading ? 'Setting Password...' : 'Set New Password'}
          </button>
        </form>
      </>
    );
  } else if (demoActive) {
    cardBody = (
      <>
        <h1 className="login-card__title">Welcome back</h1>
        <p className="login-card__subtitle">Sign in to access your workspace</p>

        <div className="demo-banner">
          <span className="demo-badge">Demo Mode</span>
          <p>Use demo credentials to explore the application</p>
        </div>

        <div className="demo-section">
          <h3>Quick Demo Login</h3>
          <p>Click a role to login instantly:</p>
          <div className="demo-buttons">
            <button onClick={() => handleDemoLogin('employee')} className="demo-button employee" disabled={isLoading}>
              <span style={{ fontWeight: 600 }}>Employee</span>
              <span>Submit &amp; track expenses</span>
            </button>
            <button onClick={() => handleDemoLogin('manager')} className="demo-button manager" disabled={isLoading}>
              <span style={{ fontWeight: 600 }}>Manager</span>
              <span>Approve team expenses</span>
            </button>
            <button onClick={() => handleDemoLogin('admin')} className="demo-button admin" disabled={isLoading}>
              <span style={{ fontWeight: 600 }}>Admin</span>
              <span>Full system access</span>
            </button>
          </div>
          <div className="demo-credentials">
            <p><strong>Password for all:</strong> demo123</p>
          </div>
        </div>
      </>
    );
  } else {
    cardBody = (
      <>
        <h1 className="login-card__title">{greeting}</h1>
        <p className="login-card__subtitle">{signInSub}</p>

        {signedOutForInactivity && !error && (
          <div className="info-message" role="status">
            You were signed out due to inactivity. Please sign in again.
          </div>
        )}

        {error && <div className="error-message" role="alert">{error}</div>}

        {isSSOEnabled && (
          <div className="login-card__sso">
            <MicrosoftSignInButton
              onClick={handleSSOLogin}
              loading={isLoading}
              checkingSession={authLoading}
              disabled={busy}
            />
            <p className="login-card__hint">Use your organization Microsoft account to sign in</p>
            <div className="login-card__security">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                <path d="M9 12l2 2 4-4" />
              </svg>
              <span>Secured by Microsoft Entra ID · Enterprise SSO</span>
            </div>
            <div className="login-card__rule" aria-hidden="true" />
            <p className="login-card__support">Trouble signing in? Contact your IT administrator.</p>
          </div>
        )}

        {/* SSO is the only sign-in path once it is configured; the native form is the
            fallback for builds without REACT_APP_COGNITO_DOMAIN. */}
        {!isSSOEnabled && (
          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="Enter your email"
                disabled={isLoading}
                autoComplete="username"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Enter your password"
                disabled={isLoading}
                autoComplete="current-password"
              />
            </div>

            <button type="submit" disabled={busy || !email || !password} className="login-button">
              {isLoading ? 'Signing in...' : authLoading ? 'Checking session...' : 'Sign in'}
            </button>
          </form>
        )}
      </>
    );
  }

  return (
    <div className="login-page">
      {/* Page-wide background: one continuous gradient environment with large
          blurred orbs. Decorative only. */}
      <div className="login-bg" aria-hidden="true">
        <span className="login-bg__grid" />
        <span className="login-bg__orb login-bg__orb--emerald" />
        <span className="login-bg__orb login-bg__orb--mint" />
        <span className="login-bg__orb login-bg__orb--cyan" />
        <span className="login-bg__orb login-bg__orb--accent" />
        <span className="login-bg__orb login-bg__orb--glow" />
        <span className="login-bg__facet login-bg__facet--a" />
        <span className="login-bg__facet login-bg__facet--b" />
      </div>

      {/* Three balanced areas: brand (left), module visualization (centre),
          sign-in card (right). They stack on narrow screens. */}
      <div className="login-shell">
        {/* ── Left: branding + headline ── */}
        <header className="login-head">
          <div className="login-head__brand">
            <img src="/ci-logo.png" alt="" className="login-head__logo" width="40" height="40" />
            <span className="login-head__name">CreyenteOne</span>
          </div>
          <span className="login-head__eyebrow">
            <span className="login-head__live" aria-hidden="true" />
            Your workday, live · {period}
          </span>
          <h1 className="login-head__title">
            Every hour of the workday. <span className="login-head__accent">One place.</span>
          </h1>
          <p className="login-head__sub">
            From the morning punch to the last timesheet, CreyenteOne keeps your people, time and performance in step.
          </p>
          <span className="login-head__rule" aria-hidden="true" />
        </header>

        {/* ── Below: module visualization (left) + sign-in card (right) ── */}
        <div className="login-body">
          <div className="login-illus">
            <WorkspaceScene />
          </div>

          <main className="login-panel">
            <div className="login-card">
              <div className="login-card__brand">
                <img src="/ci-logo.png" alt="CreyenteOne" className="login-card__logo" />
                <span className="login-card__name">CreyenteOne</span>
              </div>

              {cardBody}
            </div>
          </main>
        </div>

        <footer className="login-foot">© {new Date().getFullYear()} Creyente InfoTech. All rights reserved.</footer>
      </div>
    </div>
  );
};

export default LoginForm;
